import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Package, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../context/AuthContext';
import FedExPackagingPicker from '../../carriers/FedExPackagingPicker';
import {
  DEFAULT_FEDEX_PACKAGING_ID,
  getFedExPackagingOption,
  type FedExPackagingId,
} from '../../carriers/fedexPackagingCatalog';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type RateOption = {
  provider: 'FEDEX' | 'UPS';
  serviceCode: string;
  serviceName: string;
  amountMinor: number;
  currency: string;
  transitDays?: number;
};

type CarrierShipment = {
  id: string;
  provider: string;
  status: string;
  masterTrackingNumber?: string | null;
  serviceName?: string | null;
  trackingEvents?: Array<{
    occurredAt: string;
    description?: string | null;
    location?: string | null;
  }>;
};

type Props = {
  orderId: string;
  orderStatus: string;
  addressVerified: boolean;
  carrierServiceCode?: string | null;
  carrierServiceName?: string | null;
  estimatedWeightLbs?: number;
  isInternational?: boolean;
  compact?: boolean;
};

function formatFedExQuoteError(message: string): string {
  if (message.includes('ACCOUNT.NUMBER.MISMATCH')) {
    return 'FedEx account number does not match your API project. In Settings → Carriers, use the exact account linked at developer.fedex.com (sandbox test account: 740561073).';
  }
  return message;
}

function parseApiError(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return formatFedExQuoteError(message);
    }
    if (Array.isArray(message) && typeof message[0] === 'string') {
      return formatFedExQuoteError(message[0]);
    }
  }
  return fallback;
}

export default function OrderFulfillmentPanel({
  orderId,
  orderStatus,
  addressVerified,
  carrierServiceCode,
  carrierServiceName,
  estimatedWeightLbs,
  isInternational = false,
  compact = false,
}: Props) {
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const canShip =
    auth.type === 'ADMIN' || (auth.permissions || []).includes('CARRIER_SHIPMENT:CREATE');
  const [handoff, setHandoff] = useState<'PICKUP' | 'DROP_OFF'>('DROP_OFF');
  const [weightLbs, setWeightLbs] = useState(
    () => Math.max(0.5, Number(estimatedWeightLbs ?? 2)).toFixed(1),
  );
  const [packagingType, setPackagingType] = useState<FedExPackagingId>(DEFAULT_FEDEX_PACKAGING_ID);
  const [quotes, setQuotes] = useState<RateOption[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<RateOption | null>(null);
  const [shipments, setShipments] = useState<CarrierShipment[]>([]);
  const [quoting, setQuoting] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [busyShipmentId, setBusyShipmentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (estimatedWeightLbs != null && estimatedWeightLbs > 0) {
      setWeightLbs(Math.max(0.5, Number(estimatedWeightLbs)).toFixed(1));
    }
  }, [estimatedWeightLbs]);

  const loadShipments = useCallback(async () => {
    const res = await authorizedFetch(`${API_BASE}/carriers/shipments/orders/${orderId}`, {
      headers: { 'x-department-slug': '' },
    });
    if (res.ok) setShipments((await res.json()) as CarrierShipment[]);
  }, [authorizedFetch, orderId]);

  useEffect(() => {
    void loadShipments();
  }, [loadShipments]);

  const canQuoteOrShip = orderStatus === 'PAID' || orderStatus === 'RESHIP_PENDING';
  const hasActiveShipment = shipments.some((s) => !['VOIDED', 'CANCELLED'].includes(s.status));

  const maxPackagingLbs = useMemo(() => {
    const opt = getFedExPackagingOption(packagingType);
    return opt?.maxWeightLbs ?? 50;
  }, [packagingType]);

  if (!canQuoteOrShip && shipments.length === 0) return null;

  if (!addressVerified && canQuoteOrShip && !hasActiveShipment) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        {t('backoffice.carrier_address_required', {
          defaultValue: 'Verify the delivery address before creating a FedEx label.',
        })}
      </p>
    );
  }

  const parcelsPayload = () => [
    {
      sequenceIndex: 1,
      weightLbs: Math.min(maxPackagingLbs, Math.max(0.1, Number(weightLbs) || 1)),
      packagingType,
    },
  ];

  const quote = async () => {
    setQuoting(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/carriers/shipments/orders/${orderId}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-department-slug': '' },
        body: JSON.stringify({ parcels: parcelsPayload() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(parseApiError(body, 'Quote failed'));
      const fedexOnly = (body as RateOption[]).filter((q) => q.provider === 'FEDEX');
      const data = fedexOnly.length > 0 ? fedexOnly : (body as RateOption[]);
      if (data.length === 0) throw new Error('No FedEx rates returned for this shipment.');
      setQuotes(data);
      const preferred =
        (carrierServiceCode
          ? data.find((q) => q.serviceCode === carrierServiceCode && q.provider === 'FEDEX')
          : null) ||
        data[0] ||
        null;
      setSelectedQuote(preferred);
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Quote failed';
      setError(formatFedExQuoteError(raw));
    } finally {
      setQuoting(false);
    }
  };

  const ship = async () => {
    if (!selectedQuote || !canShip || shipping) return;
    setShipping(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/carriers/shipments/orders/${orderId}/ship`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-department-slug': '' },
        body: JSON.stringify({
          provider: selectedQuote.provider,
          serviceCode: selectedQuote.serviceCode,
          handoffMode: handoff,
          parcels: parcelsPayload(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(parseApiError(body, 'Label creation failed'));
      await loadShipments();
      setQuotes([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ship failed');
    } finally {
      setShipping(false);
    }
  };

  const voidShipment = async (shipmentId: string) => {
    setBusyShipmentId(shipmentId);
    try {
      await authorizedFetch(`${API_BASE}/carriers/shipments/${shipmentId}/void`, {
        method: 'POST',
        headers: { 'x-department-slug': '' },
      });
      await loadShipments();
    } finally {
      setBusyShipmentId(null);
    }
  };

  const openLabel = async (shipmentId: string) => {
    setBusyShipmentId(shipmentId);
    try {
      const res = await authorizedFetch(`${API_BASE}/carriers/shipments/${shipmentId}/label`, {
        headers: { 'x-department-slug': '' },
      });
      if (!res.ok) throw new Error('label');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Unable to open FedEx label');
    } finally {
      setBusyShipmentId(null);
    }
  };

  const controlsDisabled = quoting || shipping;

  return (
    <section
      className={[
        compact
          ? 'rounded-lg border border-sky-100 bg-sky-50/30 p-2.5'
          : 'rounded-xl border border-sky-200/80 bg-gradient-to-b from-sky-50/60 to-white p-4',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5">
          <Truck className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />
          <h3 className={compact ? 'text-xs font-semibold' : 'text-sm font-semibold'}>
            {t('backoffice.carrier_fedex_fulfillment', { defaultValue: 'FedEx shipping' })}
          </h3>
        </div>
        {carrierServiceName ? (
          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-sky-800 ring-1 ring-sky-200">
            {carrierServiceName}
          </span>
        ) : null}
      </div>

      {canQuoteOrShip && !hasActiveShipment ? (
        <div className={compact ? 'mt-2 space-y-2' : 'mt-3 space-y-3'}>
          <FedExPackagingPicker
            value={packagingType}
            onChange={setPackagingType}
            isInternational={isInternational}
            disabled={controlsDisabled}
            compact={compact}
          />
          <p className="text-[10px] leading-snug text-slate-500">
            {t('backoffice.carrier_box_rate_hint', {
              defaultValue:
                'Box size is saved for the label. Sandbox quotes use weight-based test rates; production uses One Rate flat pricing for the selected box.',
            })}
          </p>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,4rem)_minmax(0,1fr)_auto_auto] sm:items-end">
          <label className="text-[10px] font-medium text-slate-600">
            {t('backoffice.carrier_weight_lbs', { defaultValue: 'Weight (lb)' })}
            <input
              type="number"
              min={0.1}
              max={maxPackagingLbs}
              step={0.1}
              className="mt-0.5 w-full rounded border border-slate-300 px-1.5 py-1 text-xs disabled:bg-slate-50"
              value={weightLbs}
              disabled={controlsDisabled}
              onChange={(e) => setWeightLbs(e.target.value)}
            />
          </label>
          <label className="text-[10px] font-medium text-slate-600">
            {t('backoffice.carrier_handoff', { defaultValue: 'Handoff' })}
            <select
              className="mt-0.5 w-full rounded border border-slate-300 px-1.5 py-1 text-xs disabled:bg-slate-50"
              value={handoff}
              disabled={controlsDisabled}
              onChange={(e) => setHandoff(e.target.value as 'PICKUP' | 'DROP_OFF')}
            >
              <option value="DROP_OFF">Drop-off</option>
              <option value="PICKUP">Pickup</option>
            </select>
          </label>
          <button
            type="button"
            disabled={controlsDisabled}
            onClick={() => void quote()}
            className="inline-flex h-7 items-center justify-center gap-1 rounded border border-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-800 disabled:opacity-50"
          >
            {quoting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {quoting
              ? t('backoffice.carrier_fetching_rates', { defaultValue: 'Rates…' })
              : t('backoffice.carrier_get_rates', { defaultValue: 'Get rates' })}
          </button>
          {canShip && selectedQuote ? (
            <button
              type="button"
              disabled={controlsDisabled}
              onClick={() => void ship()}
              className="inline-flex h-7 items-center justify-center gap-1 rounded bg-sky-700 px-2 text-[11px] font-semibold text-white disabled:opacity-50"
            >
              {shipping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Package className="h-3.5 w-3.5" />}
              {shipping
                ? t('backoffice.carrier_creating_label', { defaultValue: 'Label…' })
                : t('backoffice.carrier_create_label', {
                    defaultValue: 'Create label',
                    provider: 'FedEx',
                  })}
            </button>
          ) : null}
          </div>
        </div>
      ) : null}

      {quotes.length > 0 && canQuoteOrShip && !hasActiveShipment ? (
        <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
          {quotes.map((q) => (
            <button
              key={`${q.provider}:${q.serviceCode}`}
              type="button"
              disabled={controlsDisabled}
              onClick={() => setSelectedQuote(q)}
              className={[
                'block w-full rounded-md border px-2 py-1.5 text-left text-xs disabled:opacity-50',
                selectedQuote?.serviceCode === q.serviceCode
                  ? 'border-sky-600 bg-sky-50 text-sky-900'
                  : 'border-slate-200 bg-white text-slate-700',
              ].join(' ')}
            >
              {q.serviceName} · {(q.amountMinor / 100).toFixed(2)} {q.currency}
              {q.transitDays != null ? ` · ~${q.transitDays}d` : ''}
            </button>
          ))}
        </div>
      ) : null}

      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      {shipments.length > 0 ? (
        <div className="mt-3 space-y-2 border-t border-sky-100 pt-3">
          {shipments.map((s) => (
            <div
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs"
            >
              <div className="min-w-0">
                <span className="font-semibold text-slate-900">{s.status}</span>
                {s.masterTrackingNumber ? (
                  <span className="ml-2 font-mono text-slate-600">{s.masterTrackingNumber}</span>
                ) : null}
                {s.serviceName ? (
                  <span className="ml-2 text-slate-500">{s.serviceName}</span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {s.status !== 'VOIDED' ? (
                  <button
                    type="button"
                    disabled={busyShipmentId === s.id}
                    className="font-semibold text-sky-700 underline disabled:opacity-50"
                    onClick={() => void openLabel(s.id)}
                  >
                    Print label
                  </button>
                ) : null}
                {s.status !== 'VOIDED' && canShip ? (
                  <button
                    type="button"
                    disabled={busyShipmentId === s.id}
                    className="font-semibold text-rose-700 underline disabled:opacity-50"
                    onClick={() => void voidShipment(s.id)}
                  >
                    Void
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : canQuoteOrShip && !compact ? (
        <p className="mt-2 text-[11px] text-slate-500">
          {t('backoffice.carrier_batch_hint', {
            defaultValue:
              'Add this order to a shipping batch, confirm weight, then create the FedEx label.',
          })}
        </p>
      ) : null}
    </section>
  );
}
